import {
  UserType,
  ChecklistItem,
  CursorPagingOptions,
  AggregateOfImpactByProject,
  ImpactType,
  ImpactTile,
} from "@ecologi/shared";
import { fetchFromAPI } from "./fetch-from-api";
import { UserInfoResponse } from "openid-client";
import { uploadS3Direct } from "./s3";
import { resolvePublicProjectsAssetUrls } from "../projects/public-project";

/**
 * @param key The key for use with React Query
 * @param accessToken Auth0 access token for server-side requests
 */
export const getLoggedInUser = async (
  key?: string | undefined,
  accessToken?: string
): Promise<UserType> => {
  const auth0Session = await getAuth0SessionData();

  if (!auth0Session) {
    throw new Error("Auth0 session not found");
  }

  return fetchFromAPI(`/auth/login`, { method: "POST" }, accessToken);
};

export interface FieldsToUpdate {
  billingAddress1?: string;
  billingAddress2?: string;
  billingCity?: string;
  billingCompanyName?: string;
  billingCountry?: string;
  billingEmail?: string;
  billingPostcode?: string;
  blurb?: string;
  checklistItems?: ChecklistItem[];
  city?: string;
  country?: string;
  firstName?: string;
  groupName?: string;
  email?: string;
  password?: string;
  isPrivate?: boolean;
  lastName?: string;
  link?: string;
  picture?: string;
  username?: string;
  forestName?: string;
  isBadgesHiddenFromProfile?: boolean;
  apiPoNumber?: string;
}

export const updateUser = async ({
  userId,
  fieldsToUpdate,
}: {
  userId: string;
  fieldsToUpdate: FieldsToUpdate;
}): Promise<{ user: UserType }> => {
  return await fetchFromAPI(`/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(fieldsToUpdate),
  });
};

export const deleteUser = async ({
  userId,
  feedback,
}: {
  userId: string;
  feedback: string;
}): Promise<any> => {
  return await fetchFromAPI(`/users/${userId}`, {
    method: "DELETE",
    body: JSON.stringify({ feedback }),
  });
};

export const uploadProfileImage = async ({
  userId,
  file,
}: {
  userId: string;
  file: Blob;
}): Promise<{ user: UserType }> => {
  const fileUploader = uploadS3Direct(() =>
    fetchFromAPI("/file-upload/profile-image", {
      body: JSON.stringify({
        mimetype: file.type,
        size: file.size,
      }),
      method: "POST",
    })
  );

  const picture = await fileUploader(file);

  // Update DB copy of user with the response containing the new asset URL
  return updateUser({ userId, fieldsToUpdate: { picture } });
};

// FIXME: change any[]
export const getInvoices = async (_, userId: string): Promise<any[]> =>
  fetchFromAPI(`/users/${userId}/invoices`);

//  UserId here should refer to the standalone profileId or the child profileId ?
export const generateCert = async (userId: string) => {
  return await fetchFromAPI(`/users/${userId}/generate-certificate`, {
    method: "POST",
  });
};

export const createAPIKey = async ({
  userId,
  ...body
}: {
  userId: string;
  description: string;
}): Promise<UserType> => {
  return await fetchFromAPI(`/users/${userId}/create-api-key`, {
    method: "POST",
    body: JSON.stringify(body),
  });
};

export const verifyEmail = async (
  _,
  token: string
): Promise<{ updatedUser: UserType }> => {
  return await fetchFromAPI(`/auth/verify-email`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
};

export const getAuth0SessionData =
  async (): Promise<UserInfoResponse | null> => {
    const response = await fetch("/auth/profile");

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to load Auth0 session: ${response.status}`);
    }

    return response.json();
  };

export const getUserTilesPaginated = async (
  _: string,
  userId: string,
  take: number,
  cursor: CursorPagingOptions,
  includePending: boolean
) => {
  const query = new URLSearchParams({
    cursor: cursor.id.toString(),
    cursorDirection: cursor.direction,
    cursorInclusive: cursor.isInclusive.toString(),
    take: take.toString(),
    includePending: includePending.toString(),
  });

  return await fetchFromAPI(`/users/${userId}/tiles?${query.toString()}`);
};

export const getAggregateOfCarbonOffsetsByProject = async (
  userId: string
): Promise<AggregateOfImpactByProject<ImpactType.CARBON_OFFSETS>> => {
  const data = await fetchFromAPI<
    AggregateOfImpactByProject<ImpactType.CARBON_OFFSETS>
  >(`/users/${userId}/aggregate-of-carbon-offsets-by-project`);

  return { ...data, projects: resolvePublicProjectsAssetUrls(data.projects) };
};

export const getAggregateOfCarbonRemovalsByProject = async (
  userId: string
): Promise<AggregateOfImpactByProject<ImpactType.CARBON_REMOVAL>> => {
  const data = await fetchFromAPI<
    AggregateOfImpactByProject<ImpactType.CARBON_REMOVAL>
  >(`/users/${userId}/aggregate-of-carbon-removals-by-project`);

  return { ...data, projects: resolvePublicProjectsAssetUrls(data.projects) };
};

export const getAggregateOfHabitatRestorationsByProject = async (
  userId: string
): Promise<AggregateOfImpactByProject<ImpactType.HABITAT_RESTORATION>> => {
  const data = await fetchFromAPI<
    AggregateOfImpactByProject<ImpactType.HABITAT_RESTORATION>
  >(`/users/${userId}/aggregate-of-habitat-restorations-by-project`);

  return { ...data, projects: resolvePublicProjectsAssetUrls(data.projects) };
};

export const getTile = async (
  userId: string,
  tileId: string
): Promise<ImpactTile> => fetchFromAPI(`/users/${userId}/tile/${tileId}`);
